import type { NavigateFunction } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';
import { SplashScreen } from '@capacitor/splash-screen';

export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform();

export async function initCapacitor() {
  if (!isNative) return;

  // Status bar — dark bg, light text
  await StatusBar.setStyle({ style: Style.Dark });
  if (platform === 'android') {
    await StatusBar.setBackgroundColor({ color: '#0a1a0f' });
  }

  // Android hardware back button
  App.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) {
      window.history.back();
    } else {
      App.exitApp();
    }
  });

  // Deep link handling
  App.addListener('appUrlOpen', ({ url }) => {
    try {
      const path = new URL(url).pathname;
      if (path) window.location.href = path;
    } catch { /* ignore malformed URLs */ }
  });

  // Track keyboard height for chat input positioning
  Keyboard.addListener('keyboardWillShow', (info) => {
    document.documentElement.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
  });
  Keyboard.addListener('keyboardWillHide', () => {
    document.documentElement.style.setProperty('--keyboard-height', '0px');
  });

  // Hide splash after app mounts
  await SplashScreen.hide();
}

/**
 * Open a URL on all platforms.
 * - Native: opens in Capacitor Browser (in-app browser, not WebView hijack).
 *   Relative paths are resolved against https://satguru.io.
 * - Web + relative path + navigate: uses React Router for SPA navigation.
 * - Web + absolute URL: window.location.href (e.g. Stripe billing portal).
 */
export async function openUrl(url: string, navigate?: NavigateFunction): Promise<void> {
  if (isNative) {
    const { Browser } = await import('@capacitor/browser');
    const abs = url.startsWith('http') ? url : `https://satguru.io${url}`;
    await Browser.open({ url: abs });
  } else if (navigate && !url.startsWith('http')) {
    navigate(url);
  } else {
    window.location.href = url;
  }
}
